import React from "react";
import { useClient } from "sanity";
import { DashboardWidgetContainer } from "@sanity/dashboard";
import { useQuery } from "@tanstack/react-query";
import styled from "styled-components";
import { Label } from "@sanity/ui";
import { PreviewNotice, previewRows } from "../commons/previewRows";

const ValueLabel = styled(Label)`
  color: var(--card-muted-fg-color);
  text-align: left;
  white-space: wrap;
  font-weight: 400;
`;

const containerStyle = { margin: "10px" };

const DescriptionCell = styled.td`
  max-width: 320px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export type LandProgrammeExportRow = {
  voyage_cod: string;
  sail_date: string;
  land_cod: string;
  pre_post: "PRE" | "PST";
  duration: number | null;
  description: string | null;
};

const csvCell = (value: string | number | null | undefined) =>
  `"${(value ?? "").toString().replace(/"/g, '""')}"`;

export type LandProgrammeExporterProps<T> = {
  query: string;
  title: string;
  mapResult: (data: T) => LandProgrammeExportRow[];
};

export const LandProgrammeExporter = <T,>({
  query,
  mapResult,
  title
}: LandProgrammeExporterProps<T>) => {
  const client = useClient({ apiVersion: "2021-10-21" });

  const result = useQuery({
    queryKey: ["land-programme-exporter", query],
    queryFn: async () => client.fetch<T[]>(query),
    enabled: !!query,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return (
    <DashboardWidgetContainer header={title}>
      {result.isPending && (
        <div style={containerStyle}>Loading...</div>
      )}
      {result.status === "error" && (
        <div style={{ ...containerStyle, color: "yellow" }}>
          Error loading data. Try to refresh the page or contact dev team
        </div>
      )}
      {result.status === "success" && (
        <LoadedLandProgrammeExporter
          rows={(result.data ?? []).flatMap(mapResult)}
          title={title}
        />
      )}
    </DashboardWidgetContainer>
  );
};

type LoadedLandProgrammeExporterProps = {
  rows: LandProgrammeExportRow[];
  title: string;
};

const LoadedLandProgrammeExporter: React.FC<
  LoadedLandProgrammeExporterProps
> = ({ rows, title }) => {
  const handleExportCSV = () => {
    if (rows.length === 0) return;

    const headers = [
      "Voyage code",
      "Sail date",
      "Land code",
      "Pre/Post",
      "Duration",
      "Description"
    ];
    const csvContent = [
      headers.join(","),
      ...rows.map((row) =>
        [
          csvCell(row.voyage_cod),
          csvCell(row.sail_date),
          csvCell(row.land_cod),
          csvCell(row.pre_post),
          csvCell(row.duration),
          csvCell(row.description)
        ].join(",")
      )
    ].join("\n");

    const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${title.replace(/\s+/g, "_").toLowerCase()}_${new Date().toISOString().split("T")[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <>
      <button
        onClick={handleExportCSV}
        disabled={rows.length === 0}
        style={{
          margin: "10px",
          padding: "6px",
          backgroundColor: "#2276fc",
          color: "white",
          border: "none",
          borderRadius: "4px",
          cursor: rows.length > 0 ? "pointer" : "not-allowed",
          opacity: rows.length > 0 ? 1 : 0.5
        }}
      >
        Export CSV ({rows.length} elements)
      </button>
      <table style={{ borderCollapse: "separate", borderSpacing: "21px" }}>
        <thead>
          <tr style={{ textAlign: "left" }}>
            <th>Voyage Code</th>
            <th>Sail Date</th>
            <th>Land Code</th>
            <th>Pre/Post</th>
            <th>Duration</th>
            <th>Description</th>
          </tr>
        </thead>
        <tbody>
          {previewRows(rows).map((row, i) => (
            <tr key={`${row.voyage_cod}-${row.land_cod}-${row.pre_post}-${i}`}>
              <td>
                <ValueLabel muted>{row.voyage_cod}</ValueLabel>
              </td>
              <td>{row.sail_date}</td>
              <td>{row.land_cod}</td>
              <td>{row.pre_post}</td>
              <td>{row.duration ?? ""}</td>
              <DescriptionCell title={row.description ?? ""}>
                {row.description ?? ""}
              </DescriptionCell>
            </tr>
          ))}
        </tbody>
      </table>
      <PreviewNotice total={rows.length} />
    </>
  );
};
